import React, { useEffect, useRef } from 'react';
import { useScope } from '../context/ScopeContext';
import { useAuth } from '../context/AuthContext';
import { Course, UserRole } from '../types';
import { scopeCounts, scopeTaggedCounts } from '../lib/catalogScope';

type ScopeCourse = Pick<Course, 'id' | 'tags'>;

/**
 * The one question, asked once.
 *
 * A student who arrives with nothing to go on — no stored choice, no link
 * parameter, no enrolments — used to land on the tenant default (the NSC)
 * with a small chip in the header as the only sign that another curriculum
 * existed. An IEB learner had no reason to notice it. So on that first
 * arrival, before anything else, the page asks: which one are you on? Two
 * big buttons, one tap, remembered for good; either answer is an explicit
 * choice, so the question never comes back.
 *
 * It does not fire when the answer is already knowable. `undecidedAxes` is
 * empty until storage, the URL and the enrolments have all been read, and it
 * lists only axes none of them settled. And it does not fire when there is
 * nothing to choose between: the same live-values rule as the switcher — a
 * value no published course carries is not offered, so while the IEB courses
 * are unpublished the question stays away rather than offering a trapdoor.
 *
 * Staff are never asked; the scope does not apply to them.
 */
export const ScopeFirstChoice: React.FC<{ courses: ScopeCourse[] }> = ({ courses }) => {
  const { user } = useAuth();
  const { undecidedAxes, selection, setScopeValue } = useScope();
  const firstButton = useRef<HTMLButtonElement>(null);

  const isStaff = user?.role === UserRole.INSTRUCTOR || user?.role === UserRole.ADMIN;

  // One axis at a time; a second axis (there is none today) would be asked next.
  const axis = undecidedAxes.find((a) => {
    const tagged = scopeTaggedCounts(courses, a);
    return a.tags.filter((t) => (tagged[t.id] ?? 0) > 0).length > 1;
  });

  useEffect(() => {
    if (axis) firstButton.current?.focus();
  }, [axis]);

  // Escape keeps whatever they are looking at, as a real choice — so the
  // question is answered, not dismissed to reappear on the next page.
  useEffect(() => {
    if (!axis) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const current = selection[axis.category.id] ?? axis.defaultTag;
      if (current) setScopeValue(axis.category.id, current);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [axis, selection, setScopeValue]);

  if (isStaff || !axis) return null;

  const tagged = scopeTaggedCounts(courses, axis);
  const counts = scopeCounts(courses, axis);
  const options = axis.tags.filter((t) => (tagged[t.id] ?? 0) > 0);
  const name = axis.category.name.toLowerCase();

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-gray-900/60 p-4 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="scope-first-choice-title"
    >
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">Before you start</p>
        <h2 id="scope-first-choice-title" className="mt-2 text-2xl text-gray-900 sm:text-3xl">
          Which {name} are you on?
        </h2>
        <p className="mt-2 text-sm text-gray-500">
          This decides which courses you see. You can switch any time from the {axis.category.name} control at
          the top of the page.
        </p>
        <div className={`mt-6 grid gap-3 ${options.length === 2 ? 'sm:grid-cols-2' : ''}`}>
          {options.map((tag, i) => {
            const n = counts[tag.id] ?? 0;
            return (
              <button
                key={tag.id}
                ref={i === 0 ? firstButton : undefined}
                type="button"
                onClick={() => setScopeValue(axis.category.id, tag)}
                className="group flex flex-col items-start rounded-xl border-2 border-gray-200 px-5 py-4 text-left transition-colors hover:border-primary-500 hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <span className="text-xl font-bold text-gray-900 group-hover:text-primary-700">{tag.name}</span>
                <span className="mt-1 text-xs text-gray-500">
                  {n} course{n === 1 ? '' : 's'}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
